import Button from '@/shared/Button'
import FormCreator from '@/shared/FormCreator'

import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'

import { useEffect, useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import unionBy from 'lodash/unionBy'

import * as Yup from 'yup'

import DialogHeader from '@/shared/DialogHeader'
import Typography from '@mui/material/Typography'
import debounce from '@mui/material/utils/debounce'


const mapStateToProps = ({ loading }) => ({
  isLoading: loading['account/createAccount'],
  isRolesLoading: loading['account/getUserRoles'],
  isCompanyLoading: loading['account/getCompany'],
})

const inputConfig = {
  inputBaseProps: {
    labelSuffix: '', // 不加冒號或其他符號
    justifyContent: 'center', // 將 label 與 input 靠上對齊
    direction: 'column', // label 在上，input 在下
    alignItems: 'stretch', // input 撐滿整個欄位
    labelProps: {
      sx: {
        mb: -2, // 調小 label 與 input 的距離
        lineHeight: 1.2, // 行高
        fontWeight: 400, // 適中，不要太輕
        fontSize: 12, // 確保字體大小統一
      },
    },
    childrenSx: {
      width: '95%',
      mt: 0, // 去掉 input 上方的多餘間距
    },
  },
  color: 'btn_positive',
  endAdornment: args => {
    const value = args?.value || ''
    const maxLength = args?.inputProps?.maxLength || 0

    if (!maxLength) return null

    return (
      <Typography
        variant="caption"
        sx={{ color: '#00000040', fontSize: '12px !important' }}
      >{`${value.length}/${maxLength}`}</Typography>
    )
  },
}

const CreateAccountDialog = ({
  dispatch,
  isLoading,
  isRolesLoading,
  isCustodiansLoading,
  isCompanyLoading,
  onClose,
  onComplete,
  ...props
}) => {
  const companyPageRef = useRef(0)
  const [rolesResults, setRolesResults] = useState({})
  const [companyResults, setCompanyResults] = useState({})
  const form = useRef()
  const { t } = useTranslation()
  const { data: rolesResultsData = [] } = rolesResults
  const { data: companyResultsData = [] } = companyResults

  const companyId = form.current ? form.current.watch('companyName')?.value : null

  const handleClose = () => {
    const { reset } = form.current
    reset?.()
    onClose?.()
  }

  const handleSubmit = () => {
    const { handleSubmit: formSubmit } = form.current
    formSubmit?.(values => {
      const { email, phone, roleId, employeeId, status } = values
      dispatch({
        type: 'account/createAccount',
        payload: {
          email,
          phone,
          companyId,
          employeeId,
          roleId,
          status,
        },
        successCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: { message: t('messages.account_created'), severity: 'success' },
          })
          onClose?.()
          onComplete?.()
        },
        errorCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: {
              message: t('messages.account_create_failed'),
              severity: 'error',
            },
          });
        },
      })
    })?.()
  }

  const getRoles = () => {
    if (!isRolesLoading) {
      dispatch({
        type: 'account/getUserRoles',
        successCallback: response => {
          const { data: responseData = [] } = response || {}

          const newData = responseData.map(d => ({ value: d.id, label: d.roleName }))

          setRolesResults(() => ({
            data: newData,
          }))
        },
      })
    }
  }

  const getCompany = params => {
    const { page = 0, keyword = '' } = params || {}

    if (!isCompanyLoading) {
      companyPageRef.current = page

      dispatch({
        type: 'account/getCompany',
        payload: {
          pageIndex: companyPageRef.current,
          pageSize: 10,
          keyword, //  關鍵字
        },
        successCallback: response => {
          const { data: responseData = [], paging: responsePaging = {} } = response || {}
          const { pageIndex } = responsePaging

          const newData = responseData.map(d => ({
            value: d.companyId,
            label: d.companyName,
          }))

          setCompanyResults(s => ({
            data: pageIndex > 0 ? unionBy(s.data || [], newData, 'value') : newData,
            paging: responsePaging,
          }))
        },
      })
    }
  }

  const fetchCompanyData = useCallback((companyNameValue) => {
    const keyword = companyNameValue || form.current?.getValues()?.companyName?.label
    const payload = {
      pageIndex: companyPageRef.current,
      pageSize: 10,
      keyword: keyword || '',
    }

    // 回傳 dispatch 的 Promise
    return new Promise(resolve => {
      dispatch({
        type: 'account/getCompany',
        payload,
        successCallback: response => {
          const { data: responseData = [], paging: responsePaging = {} } = response || {}
          const { pageIndex } = responsePaging

          const newData = responseData.map(d => ({
            value: d.companyId,
            label: d.companyName,
            startTime: d.startTime,  //  假設 API 回傳這兩個
            endTime: d.endTime,
          }))

          setCompanyResults(s => ({
            data: pageIndex > 0
              ? unionBy(s.data || [], newData, 'value')
              : newData,
            paging: responsePaging,
          }))

          resolve(newData)  //  將資料 resolve 出去
        },
      })
    })
  }, [dispatch])

  const setCompanyNameDebounce = debounce((_, value) => {
    companyPageRef.current = 0
    fetchCompanyData(value)
  }, 400)

  useEffect(() => {
    if (props.open) {
      getRoles()
      getCompany()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open])

  return (
    <Dialog {...props} maxWidth={'xs'}
      PaperProps={{
        sx: {
          borderRadius: 5, // ← 圓角在這裡才有效
          overflow: 'hidden', //  必加，不然 Header 會蓋掉圓角
        },
      }}>

      <DialogHeader
        title={t('general.add_account')}
        onClose={handleClose}
      />

      <DialogContent sx={{ width: 450, pr: 1 }}>
        <FormCreator
          ref={form}
          gridItemProps={{ md: 12 }}
          formConfig={{ mode: 'submit' }}
          formItems={[
            {
              label: t('general.account_name'),
              name: 'email',
              defaultValue: '',
              validator: Yup.string()
                .required(t('validation.email_required'))
                .email(t('validation.email_format')),
              inputConfig: {
                ...inputConfig,
                inputProps: { maxLength: 40 }
              },
            },
            {
              label: t('general.phone'),
              name: 'phone',
              validator: Yup.string()
                .required(t('validation.phone_required'))
                .matches(/^(09\d{8})?$/, t('validation.phone_ten_digits_start_zero_nine'))
                .test('check-starting-digits', 'Phone number must start with "09"', value => {
                  if (!value) return true
                  return value.startsWith('09')
                })
                .test('check-number-length', 'Phone number must have exactly 10 digits', value => {
                  if (!value) return true
                  return value.replace(/[^0-9]/g, '').length === 10
                }),
              defaultValue: '',
              inputConfig: {
                ...inputConfig,
                inputProps: { maxLength: 10 }
              },
            },
            {
              label: t('general.company_name'),
              name: 'companyName',
              type: 'autocomplete',
              defaultValue: '',
              validator: Yup.mixed()
                .test(
                  'required',
                  t('validation.company_required'),
                  value => value !== null && value !== undefined && value !== ''
                ),
              inputConfig: {
                ...inputConfig,
                options: companyResultsData,
                isOptionEqualToValue: (option, value) => option?.value === value?.value,
                onChange: selectedOption => {
                  form.current.setValue('companyName', selectedOption || null) // ⭐ 同步到 form
                  companyPageRef.current = 0
                  fetchCompanyData(selectedOption?.label).then(companyData => {
                    const { startTime, endTime } = companyData?.[0] || {}
                    form.current.setValue('dateRangeFilter', {
                      startDate: startTime ? new Date(startTime) : null,
                      endDate: endTime ? new Date(endTime) : null,
                    })
                  })
                },
                onInputChange: (_, inputValue) => {
                  setCompanyNameDebounce(_, inputValue) //  debounce 處理 keyword
                },
              },
            },
            {
              label: t('general.role'),
              name: 'roleId',
              type: 'select',
              defaultValue: '1',
              validator: Yup.string()
                .required(t('validation.account_type_required')),
              inputConfig: {
                ...inputConfig,
                options: rolesResultsData,
              },
            },
            companyId === 1
              ? {
                label: t('general.employeeId'),
                name: 'employeeId',
                validator: Yup.string()
                  .required(t('validation.employeeId_required'))
                  .matches(/^\d{7}$/, t('validation.employeeId_7_digits')),
                defaultValue: '',
                inputConfig: {
                  ...inputConfig,
                  inputProps: {
                    maxLength: 7,
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  },
                },
              }
              : null,
            companyId !== 1
              ? {
                label: t('general.authorization_period'),
                type: 'labelOnly',
                hidden: companyId === 1,
              }
              : null,
            companyId !== 1
              ? {
                // label: t('授權期間'),
                name: 'dateRangeFilter',
                type: 'dateRange',
                disabled: true,
                defaultValue: { startDate: new Date(), endDate: new Date() },
              }
              : null,
            {
              label: t('general.status'),
              name: 'status',
              type: 'select',
              defaultValue: 'A',
              inputConfig: {
                ...inputConfig,
                options: [
                  { label: t('status.ACTIVE'), value: 'A' },
                  { label: t('status.INACTIVE'), value: 'IA' },
                ],
              },
            },
          ].filter(Boolean)}
          submitComponent={() => null}
          resetComponent={() => null}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          justifyContent: 'flex-end', //  右下角
        }}>
        <Button
          style={{ minWidth: 100, backgroundColor: '#2D2D6C' }}
          loading={isLoading}
          disabled={isLoading}
          onClick={async () => {
            handleSubmit()
          }}
        >
          {t('general.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default connect(mapStateToProps)(CreateAccountDialog)